import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import GoogleMapsScriptTag from 'components/GoogleMapsScriptTag';
import { valueEmpty } from 'helpers';
import boatLocationPayload from 'components/forms/utils/boatLocationPayload';
import {
  trackAddressFieldClick,
  trackAddressSuggestionIgnored,
  trackAddressValidationError,
  trackSuggestionSelection,
} from 'components/forms/utils/trackAddressValidation';

type Autocomplete = google.maps.places.Autocomplete;

let autocomplete: Autocomplete;

const NO_AUTOCOMPLETE_ATTRIBUTE = 'boataddressfieldnoautocomplete';

const isDefined = (value) => value !== undefined;

interface LocationInputProps {
  name: string;
  placeholder?: string;
  defaultValue?: string;
  showError: boolean;
  // This shouldn't have to be typed this narrowly, but it matches the only way
  // it's called. Feel free to widen the type if needed.
  onChange?: React.Dispatch<React.SetStateAction<boolean>>;
  withPin?: boolean;
  errorMessage: string;
  locationForm?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
}
const LocationInput: React.FC<LocationInputProps> = ({
  name,
  placeholder,
  defaultValue,
  showError,
  onChange,
  withPin,
  errorMessage,
  locationForm,
  onBlur,
  onFocus,
}: LocationInputProps) => {
  const { register, setValue, errors, watch, trigger, formState } = useFormContext();

  const [isFocused, setFocused] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [hasRelevantErrors, setHasRelevantErrors] = useState(false);
  /**
   * For analytical tracking purposes, we need to know what the user inputted. Watching the field isn't enough as
   * selecting a suggestion will change the value of the field. Unfortunately, we must track this manually.
   *
   * We use useRef as useState was unable to fetch the value in event callbacks.
   */
  const userTextInput = useRef<string>(null);
  /**
   * For analytical tracking purposes, we need to know if a user has selected a suggestion following the last time the
   * user has typed into the field.
   */
  const placeWasSelected = useRef(false);
  /**
   * Blur runs before Google fires `place_changed`. Defer the “ignored” check to
   * the next tick so `handlePlaceSelect` can flip the flag. Also ensure cleanup.
   */
  const deferredBlurCheckId = useRef<number | null>(null);
  // For safety's sake, clear the timeout when the component unmounts.
  useEffect(
    () => () => {
      if (deferredBlurCheckId.current) {
        clearTimeout(deferredBlurCheckId.current);
      }
    },
    []
  );

  useEffect(() => {
    setTimeout(() => {
      // @ts-expect-error: Existing
      if (scriptLoaded || window.mapsLoaded) {
        handleScriptLoad();
      }
    }, 500);
  }, [scriptLoaded]);

  useEffect(() => {
    const countryCode = watch('country_code');
    if (countryCode && locationForm && autocomplete) {
      const setCountryAutocomplete = countryCode.toLowerCase();
      autocomplete.setComponentRestrictions({ country: setCountryAutocomplete });
    }
  }, [locationForm, watch]);

  const forceValidation = useCallback(async () => {
    const isUS = watch('country_code') === 'US';
    // trigger forces react-hook-form to run validation, giving feedback at
    // the time that this callback runs.
    const triggerList = ['address', 'street_1', 'city', 'state', 'postal_code', 'country_code'];
    if (isUS) triggerList.push('street_number');
    await trigger(triggerList);

    setHasRelevantErrors(
      isDefined(errors.address) ||
        // Only check the street_number error if the country is US.
        (isUS && isDefined(errors.street_number)) ||
        isDefined(errors.street_1) ||
        isDefined(errors.city)
    );
  }, [errors, trigger]);

  const handlePlaceSelect = useCallback(async () => {
    placeWasSelected.current = true;
    const place = autocomplete.getPlace();

    if (place) {
      // Important change: Set noPlaceSelected to false directly
      if (onChange) onChange(false);
      let formattedAddress = place.formatted_address;

      if (place.geometry) {
        const coords = place.geometry.location;
        formattedAddress = place.formatted_address;
        // remove the country
        if (formattedAddress.split(',').length > 1) {
          formattedAddress = formattedAddress.slice(0, formattedAddress.lastIndexOf(','));
        }

        const latlng = { lat: coords.lat(), lng: coords.lng() };

        setValue('lat', coords.lat());
        setValue('lng', coords.lng());
        setValue('owner_pin_latitude', coords.lat());
        setValue('owner_pin_longitude', coords.lng());
        setValue('near', formattedAddress);

        const values = boatLocationPayload(place, latlng);

        // Iterates of the address components and sets them in the form
        Object.entries(values).forEach(async (component) => {
          setValue(component[0], component[1]);
        });
      }
    }

    trackSuggestionSelection({ currentUserTextInput: userTextInput.current, place });

    // trigger forces react-hook-form to run validation, giving feedback at
    // the time that this callback runs.
    await forceValidation();

    trackAddressValidationError({
      currentUserTextInput: userTextInput.current,
      place,
      formErrors: errors,
      visibleValidationError: errorMessage,
    });
  }, [name, onChange, setValue, watch, forceValidation]);

  const handleScriptLoad = useCallback(() => {
    const options = {
      types: ['geocode', 'establishment'],
    };

    const inputEl = document.getElementById(name) as HTMLInputElement;

    if (window.google) {
      autocomplete = new window.google.maps.places.Autocomplete(inputEl, options);

      autocomplete.setFields(['address_components', 'geometry', 'formatted_address']);
      autocomplete.addListener('place_changed', handlePlaceSelect);
      if (inputEl) {
        inputEl.setAttribute('autocomplete', NO_AUTOCOMPLETE_ATTRIBUTE);
      }
    }
  }, [handlePlaceSelect, name]);

  const mapScriptLoaded = () => {
    setScriptLoaded(true);
  };

  return (
    <div className="u-posRelative">
      <GoogleMapsScriptTag strategy="afterInteractive" onLoad={mapScriptLoaded} callback="initMaps" />

      <input name="lat" type="hidden" ref={register} />
      <input name="lng" type="hidden" ref={register} />
      <label
        className={`InputText-label ${withPin ? 'InputText-label--withIcon' : ''} ${
          !hasRelevantErrors || !valueEmpty(watch(name)) || isFocused ? 'InputText-label--isFocused' : ''
        }`}
      >
        {placeholder}
      </label>
      <input
        data-testid="component-locationInput-address-input"
        id={name}
        className={`InputText InputText--withAnimatedLabel ${
          withPin ? 'InputText--withIconBig' : ''
        } u-fsSm  u-sizeFull  ${showError && hasRelevantErrors ? ' is-invalid' : ''}`}
        name={name}
        defaultValue={defaultValue}
        type="text"
        ref={register({
          validate: (value) => !valueEmpty(value),
        })}
        autoComplete="off"
        onChange={() => {
          if (onChange) onChange(true);
          placeWasSelected.current = false;
        }}
        onKeyUp={() => {
          // This updates userTextInput when the user types, but most importantly, it DOES NOT update when a suggestion
          // is selected. This keeps the userTextInput updated with only what the user manually enters into the field.
          userTextInput.current = watch(name);
        }}
        placeholder=""
        onBlur={async () => {
          setFocused(false);
          if (onBlur) {
            onBlur();
          }

          // Prior to setting a new timeout, clear the existing timeout if it exists.
          if (deferredBlurCheckId.current) clearTimeout(deferredBlurCheckId.current);

          // Given that `place_changed` fires after blur, we need to defer the check.
          deferredBlurCheckId.current = window.setTimeout(() => {
            // If a suggestion was selected, handlePlaceSelect already flipped the flag.
            const currentPlaceWasSelected = placeWasSelected.current === true;
            const currentUserTextInput = userTextInput.current;
            try {
              trackAddressSuggestionIgnored({
                currentUserTextInput,
                currentPlaceWasSelected,
              });
            } finally {
              // Reset for next interaction cycle
              placeWasSelected.current = false;
            }
          }, 150);

          await forceValidation();
        }}
        onFocus={async () => {
          // The on focus event can trigger when the field is invalid and
          // react-hook-form automatically focuses it. That's our cue to run the
          // focus handler to check if there are any errors and trigger error
          // highlighting and text.
          if (formState.dirtyFields.address) await forceValidation();

          setFocused(true);
          if (onFocus) {
            onFocus();
          }
          // Nasty hack but it works
          document.getElementById(name).setAttribute('autocomplete', 'off');

          trackAddressFieldClick();
        }}
      />
      {withPin ? (
        <svg
          height={25}
          fill="#8e9697"
          style={{ top: '25%', left: '12px', position: 'absolute' }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1008 1008"
        >
          <path d="M504 28.1c-195.1 0-352.1 157-352.1 352.1 0 180.8 290.3 542.5 323.6 585.3 4.8 9.5 19 14.3 28.6 14.3 9.5 0 19-4.8 28.6-14.3C566 922.7 856.3 561 856.3 380.2 851.4 185.2 699.1 28.1 504 28.1zM223.2 380.3c0-152.3 123.7-280.8 276-280.8S780 223.2 780 375.5c0 119-180.8 375.9-276 504.4-95.2-123.7-280.8-380.7-280.8-499.6zm347.4 0c0 38.1-28.6 66.6-66.6 66.6s-66.6-28.6-66.6-66.6 28.6-66.6 66.6-66.6 66.6 28.5 66.6 66.6z" />
        </svg>
      ) : null}

      {showError && (
        <ErrorMessage className="u-posAbsolute ControlError-list" error={hasRelevantErrors} message={errorMessage} />
      )}
    </div>
  );
};

LocationInput.defaultProps = {
  placeholder: 'Address',
  defaultValue: '',
  onChange: () => null,
  onBlur: () => null,
  onFocus: () => null,
  withPin: true,
  locationForm: false,
};

export default LocationInput;
